import type { AuthProvider, AuthRole, AuthState, AuthenticatedUser } from './model.js';
import type { OperationalProfile } from '../../authorization/model.js';

export interface MockAuthProviderOptions {
  role?: AuthRole;
  operationalProfile?: OperationalProfile;
  initiallyAuthenticated?: boolean;
}

export class MockEntraAuthProvider implements AuthProvider {
  private state: AuthState;
  private readonly listeners = new Set<(state: AuthState) => void>();
  private readonly user: AuthenticatedUser;

  constructor(options: MockAuthProviderOptions = {}) {
    this.user = createMockAuthenticatedUser(
      options.role ?? 'ADMIN',
      options.operationalProfile ?? 'GENERAL',
    );
    this.state = options.initiallyAuthenticated === true
      ? { status: 'AUTHENTICATED', user: this.user }
      : { status: 'UNAUTHENTICATED' };
  }

  getState(): AuthState {
    return this.state;
  }

  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async initialize(): Promise<void> {}

  async refreshSession(): Promise<void> {}

  async getIdentityToken(): Promise<string | null> {
    return this.state.status === 'AUTHENTICATED' ? 'mock-test-identity-token' : null;
  }

  async signIn(): Promise<void> {
    this.setState({ status: 'AUTHENTICATED', user: this.user });
  }

  async signOut(): Promise<void> {
    this.setState({ status: 'UNAUTHENTICATED' });
  }

  private setState(state: AuthState): void {
    this.state = state;
    for (const listener of this.listeners) listener(state);
  }
}

export function createMockAuthenticatedUser(
  role: AuthRole,
  operationalProfile: OperationalProfile = 'GENERAL',
): AuthenticatedUser {
  const name = role === 'OWNER'
    ? 'Jordan Owner'
    : role === 'ADMIN'
      ? 'Morgan Lee'
      : role === 'OPERATOR' ? 'Avery Operator' : 'Taylor Reed';
  const email = role === 'ADMIN'
    ? 'morgan.lee@positiveoutput.example'
    : role === 'VIEWER'
      ? 'taylor.reed@positiveoutput.example'
      : `${role.toLowerCase()}@positiveoutput.example`;
  return {
    subjectId: `mock-positive-output-${role.toLowerCase()}`,
    tenantId: 'mock-positive-output-tenant',
    tenantName: 'Positive Output',
    displayName: name,
    email,
    roles: [role],
    role,
    operationalProfile,
  };
}
